import React from 'react';

import capitalize from 'lodash/capitalize';

import { Close } from '@mui/icons-material';
import { Box, Chip, Dialog, DialogContent, DialogTitle, Grid, IconButton, Stack, Typography } from '@mui/material';

import { toDayJs } from '~/services/TimeService';

import { IMAGE_CLASSIFICATION_COLOR, IMAGE_CLASSIFICATION_LABEL, buildImageUrl } from '../constants';
import { ImageScore } from '../types';

import CurationScoreBar from './CurationScoreBar';

interface Props {
  image: ImageScore | null;
  onClose: () => void;
}

export default function CurationImageDetailDialog({ image, onClose }: Props) {
  if (!image) {
    return null;
  }

  const detailScores = [
    { label: 'Luxury Score', value: image.luxuryScore },
    { label: 'Hero Score', value: image.heroScore },
    { label: 'Lighting', value: image.lighting != null ? image.lighting * 20 : null },
    { label: 'Spaciousness', value: image.spaciousness != null ? image.spaciousness * 20 : null },
    { label: 'Material Quality', value: image.materialQuality != null ? image.materialQuality * 20 : null },
    { label: 'Cleanliness', value: image.cleanliness != null ? image.cleanliness * 20 : null },
    { label: 'Design Style', value: image.designStyle != null ? image.designStyle * 20 : null },
    { label: 'Photo Quality', value: image.photoQuality != null ? image.photoQuality * 20 : null },
  ];

  return (
    <Dialog open={!!image} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{(image.categories ?? []).map(capitalize).join(', ')}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Box
              component="img"
              src={buildImageUrl(image.imageServiceId, 800)}
              alt={(image.categories ?? []).join(', ')}
              sx={{ width: '100%', borderRadius: 1, maxHeight: 500, objectFit: 'contain' }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1}>
                <Chip
                  size="small"
                  label={IMAGE_CLASSIFICATION_LABEL[image.classification]}
                  color={IMAGE_CLASSIFICATION_COLOR[image.classification]}
                />
                <Chip size="small" label={`Hero: ${image.heroScore}`} variant="outlined" />
                <Chip size="small" label={`Luxury: ${image.luxuryScore}`} variant="outlined" />
              </Stack>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Score Breakdown
                </Typography>
                <Stack spacing={1}>
                  {detailScores.map((s) => (
                    <CurationScoreBar key={s.label} label={s.label} value={s.value} />
                  ))}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  AI Reasoning
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {image.reasoning}
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary">
                Scored: {toDayJs(image.scoredAt).format('DD MMM YYYY HH:mm')}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
